/**
 * A figure's freshness, in the vocabulary of a broker's contract note.
 *
 * A stamp rather than a hue, and that is the decision worth keeping: there are
 * eight data states, and inventing eight colours for them would leave a reader
 * learning a palette instead of reading a word. It also keeps the whole
 * surface legible to someone who cannot separate two of those hues.
 */
import type { DataState } from '@/features/mitra/api/frames'

const DAYS = ['SUN', 'MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT']

const FIXED: Record<Exclude<DataState, 'post_close_final'>, string> = {
  live: 'LIVE',
  delayed: 'DELAYED',
  stale: 'STALE',
  degraded: 'DEGRADED',
  pending: 'PENDING',
  unavailable: 'UNAVAILABLE',
  unadjusted_corporate_action: 'UNADJUSTED · CORPORATE ACTION',
  // Not a freshness claim in the way the others are: the article is from a
  // pinned artifact, so the useful question is which build it came from and
  // not which session. Saying so is still better than the LIVE it used to
  // borrow, which asserted a session it has none of.
  reference: 'REFERENCE',
}

// `Intl` names the weekday, never `Date#getDay`, which reads the host clock's
// own timezone. A 15:00 +05:45 close is still Wednesday in Kathmandu at a
// reader's midnight west of UTC-9 — `getDay` would name Wednesday's close
// "THU" for that reader, which is the wrong session for a stamp that means
// "final as of a specific NEPSE close."
const KATHMANDU_WEEKDAY = new Intl.DateTimeFormat('en-US', {
  timeZone: 'Asia/Kathmandu',
  weekday: 'short',
})

export function stampText(state: DataState, asOf: string): string {
  if (state !== 'post_close_final') return FIXED[state]
  // Only this state names a day, because only this state means "the figure is
  // final for a session that has ended" — which is a fact about a weekday, not
  // about a clock.
  const short = KATHMANDU_WEEKDAY.format(new Date(asOf)).toUpperCase()
  const day = DAYS.includes(short) ? short : ''
  return day ? `AT ${day} CLOSE` : 'AT CLOSE'
}

export function Stamp({ state, asOf }: { state: DataState; asOf: string }) {
  return (
    // THE WORDS ARE UNTOUCHED AND ONLY THE VOLUME CHANGES. `stampText`'s
    // output is asserted by exact string in seven places, in vitest and in
    // e2e, and rightly: it is the one register decision on this surface a
    // redesign must not touch, because those words are English in all three
    // catalogues and turning them into sentences would put more English on a
    // Nepali screen. What was wrong was the setting -- 9.5px mono at 0.14em
    // tracking reads as a stamp on a legal document, and the thing it
    // qualifies is a price. Same words, in the card's own face.
    <span className="text-mono-s font-semibold tracking-[0.02em] text-ink-soft">
      {stampText(state, asOf)}
    </span>
  )
}
